import { useEffect, useState } from 'react'
import * as Icons from 'lucide-react'

/* Datum-label voor een trophy/match-rij. Toont de echte datum als geldig,
   valt anders terug op de LAN-editie-naam. Voorkomt "Invalid Date" wanneer
   ended_at NULL is (bv. bij tourPodia die uit tournaments.updated_at komen
   maar wel edition_name in de meta hebben). */
function trofeeDatumLabel(r, opts = {}) {
  const raw = r.ended_at || r.created_at
  if (raw) {
    const d = new Date(raw)
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('nl-BE', opts.style === 'short'
        ? { day: '2-digit', month: '2-digit', year: '2-digit' }
        : { day: 'numeric', month: 'short', year: '2-digit' })
    }
  }
  return r.edition_name || r.lan_edition_name || '—'
}

/* Badge-icoon uit `icon_url` renderen. Kan drie vormen zijn:
   - emoji-tekst ("🔥"): render als <span> tekst
   - data-URI / http-URL / pad: render als <img>
   - leeg: return null (caller kiest fallback zoals <Award> of <Shield>) */
function renderBadgeIcoon(iconUrl, size = 'h-5 w-5') {
  if (!iconUrl) return null
  const isBeeld = typeof iconUrl === 'string'
    && (iconUrl.startsWith('http') || iconUrl.startsWith('data:') || iconUrl.startsWith('/'))
  if (isBeeld) return <img src={iconUrl} alt="" className={`${size} object-contain`} />
  return <span className={`${size} grid place-items-center text-base leading-none`}>{iconUrl}</span>
}

/* ─────────────────────────────────────────────────────────────
   HUD-frame voor profielpagina's (spelers én teams).

   Signature: vier corner-brackets in accent-kleur die het hero-blok
   omkaderen zoals een game-HUD. Font: Rajdhani condensed voor de naam,
   JetBrains Mono voor labels/handles, Space Grotesk voor rest.

   Alle blokken hieronder zijn los te gebruiken:
     <ProfileHero /> — banner + naam + stats
     <StatStrip />   — grote numerieke rij onder de hero
     <TrophyHall />  — horizontaal scrollbare kaartrij van medailles
     <AchievementPanel /> — grid van behaalde badges
     <MatchLog />    — timestamp-tabel met recente uitslagen
   ───────────────────────────────────────────────────────────── */

/* Global-css-injectie zorgt dat de fonts zichtbaar zijn en geïsoleerd
   blijven tot de profile-* klassen. Zo raakt het niet aan de rest. */
const CSS = `
.pf-hud { font-family: 'Space Grotesk', system-ui, sans-serif; }
.pf-name { font-family: 'Rajdhani', 'Space Grotesk', sans-serif;
           font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase;
           line-height: 0.9; }
.pf-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
.pf-bracket-ne, .pf-bracket-nw, .pf-bracket-se, .pf-bracket-sw {
  position: absolute; width: 40px; height: 40px; pointer-events: none;
}
.pf-bracket-nw { top: 12px; left: 12px; border-top: 2px solid; border-left: 2px solid; }
.pf-bracket-ne { top: 12px; right: 12px; border-top: 2px solid; border-right: 2px solid; }
.pf-bracket-sw { bottom: 12px; left: 12px; border-bottom: 2px solid; border-left: 2px solid; }
.pf-bracket-se { bottom: 12px; right: 12px; border-bottom: 2px solid; border-right: 2px solid; }
.pf-hero-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.02) 1px, transparent 1px);
  background-size: 24px 24px;
  mask-image: radial-gradient(ellipse at center, black 20%, transparent 75%);
}

/* ── Animated avatar frames — respect prefers-reduced-motion ── */
@keyframes pf-pulse   { 0%, 100% { box-shadow: 0 0 8px var(--fc), 0 0 0 var(--fc); }
                        50%      { box-shadow: 0 0 24px var(--fc), 0 0 4px var(--fc); } }
@keyframes pf-rotate  { to { transform: rotate(360deg); } }
@keyframes pf-neon    { 0%, 100% { filter: drop-shadow(0 0 2px var(--fc)); }
                        50%      { filter: drop-shadow(0 0 8px var(--fc)) drop-shadow(0 0 16px var(--fc)); } }
@keyframes pf-orbit   { to { transform: translate(-50%, -50%) rotate(360deg); } }
@keyframes pf-flame   { 0%   { box-shadow: 0 0 12px #ff5a5a99, 0 0 4px #ffcf40cc; }
                        50%  { box-shadow: 0 0 20px #ff9500aa, 0 0 8px #ff5a5acc; }
                        100% { box-shadow: 0 0 12px #ff5a5a99, 0 0 4px #ffcf40cc; } }
@keyframes pf-stripes { to { background-position: 32px 0; } }

.pf-frame-pulse   { animation: pf-pulse 2.4s ease-in-out infinite; }
.pf-frame-rotate  { animation: pf-rotate 6s linear infinite; }
.pf-frame-neon    { animation: pf-neon 2s ease-in-out infinite; }
.pf-frame-flame   { animation: pf-flame 1.4s ease-in-out infinite; }
.pf-frame-stripes { background-image: repeating-linear-gradient(45deg,
                    var(--fc), var(--fc) 6px, transparent 6px, transparent 12px);
                    animation: pf-stripes 1.2s linear infinite; }
.pf-orbit-dot { position: absolute; top: 50%; left: 50%; width: 12px; height: 12px;
                border-radius: 9999px; background: var(--fc);
                animation: pf-orbit 3s linear infinite; box-shadow: 0 0 12px var(--fc); }
.pf-orbit-dot.d2 { animation-delay: -1s; opacity: .7; }
.pf-orbit-dot.d3 { animation-delay: -2s; opacity: .4; }

/* ── Nieuwe premium frames ─────────────────────────────────── */

/* Lightning — flikkerende gele-witte elektrische rand */
@keyframes pf-lightning {
  0%, 92%, 100% { box-shadow: 0 0 0 2px var(--fc), 0 0 6px var(--fc); filter: brightness(1); }
  93%           { box-shadow: 0 0 0 2px #fff, 0 0 30px #fff, 0 0 60px var(--fc); filter: brightness(2); }
  94%           { box-shadow: 0 0 0 2px var(--fc), 0 0 6px var(--fc); filter: brightness(1); }
  96%           { box-shadow: 0 0 0 2px #fff, 0 0 24px #ffea70; filter: brightness(1.8); }
  97%           { box-shadow: 0 0 0 2px var(--fc), 0 0 6px var(--fc); filter: brightness(1); }
}
.pf-frame-lightning {
  background: conic-gradient(from 0deg,
    var(--fc), #fff8b0, var(--fc), #fff8b0, var(--fc));
  animation: pf-lightning 2.6s steps(1, end) infinite, pf-rotate 8s linear infinite;
  padding: 4px !important;
}

/* Aurora — vloeiende noorderlicht-band die traag rond het portret schuift */
@keyframes pf-aurora-shift { to { background-position: 400% 0; } }
.pf-frame-aurora {
  background: linear-gradient(120deg,
    #4ade80, #22d3ee, #a855f7, #ec4899, #4ade80);
  background-size: 400% 100%;
  animation: pf-aurora-shift 8s linear infinite;
  padding: 4px !important;
  filter: saturate(1.3) drop-shadow(0 0 12px #22d3ee88);
}

/* Cosmic — starfield met langzaam draaiende ring */
@keyframes pf-cosmic {
  0%   { background-position: 0% 0%, 0% 0%; }
  100% { background-position: 100% 100%, -100% 100%; }
}
.pf-frame-cosmic {
  background:
    radial-gradient(circle at 20% 30%, #fff 0.5px, transparent 1.5px),
    radial-gradient(circle at 70% 60%, #fff 0.5px, transparent 1.5px),
    conic-gradient(from 0deg, #6366f1, #a855f7, #ec4899, #6366f1);
  background-size: 20px 20px, 30px 30px, 100% 100%;
  animation: pf-cosmic 12s linear infinite, pf-rotate 20s linear infinite;
  padding: 4px !important;
  box-shadow: 0 0 24px #a855f766, inset 0 0 8px #6366f166;
}

/* Ice — koude blauw-witte kristal-glow met langzame shimmer */
@keyframes pf-ice {
  0%, 100% { box-shadow: 0 0 12px #7dd3fcaa, 0 0 24px #38bdf866, inset 0 0 6px #e0f2fe; }
  50%      { box-shadow: 0 0 20px #bae6fdcc, 0 0 40px #0ea5e988, inset 0 0 10px #f0f9ff; }
}
.pf-frame-ice {
  background:
    linear-gradient(135deg, #e0f2fe 0%, #38bdf8 30%, #0ea5e9 60%, #e0f2fe 100%);
  animation: pf-ice 3s ease-in-out infinite;
  padding: 3px !important;
}

/* Plasma — energetisch magenta/cyan met snelle roterende ring */
@keyframes pf-plasma-hue { to { filter: hue-rotate(360deg) saturate(1.5); } }
.pf-frame-plasma {
  background: conic-gradient(from 0deg,
    #ff00ff, #00ffff, #ff00ff, #00ffff, #ff00ff);
  animation: pf-rotate 3s linear infinite, pf-plasma-hue 6s linear infinite;
  padding: 4px !important;
  box-shadow: 0 0 20px #ff00ff88, 0 0 40px #00ffff44;
}

/* Void — donkere ring met paarse bliksem-glow, sprankelt */
@keyframes pf-void {
  0%, 100% { box-shadow: 0 0 8px #7c3aed, inset 0 0 6px #1e1b4b; }
  50%      { box-shadow: 0 0 24px #a855f7, 0 0 48px #7c3aed66, inset 0 0 12px #312e81; }
}
.pf-frame-void {
  background:
    radial-gradient(circle at 30% 30%, #a855f7 1px, transparent 2px),
    radial-gradient(circle at 70% 70%, #c084fc 1px, transparent 2px),
    linear-gradient(135deg, #1e1b4b, #4c1d95, #1e1b4b);
  background-size: 40px 40px, 50px 50px, 100% 100%;
  animation: pf-void 3.5s ease-in-out infinite;
  padding: 4px !important;
}

/* ── Ornate/thematische frames — decor rondom de avatar ────── */

/* Laurel — gouden laurierkrans (SVG in JSX), frame zelf is subtiel goud met glow */
@keyframes pf-laurel-shine { 0%,100% { filter: drop-shadow(0 0 4px #E4C265aa); }
                              50%    { filter: drop-shadow(0 0 12px #FDE68A) drop-shadow(0 0 20px #E4C265); } }
.pf-frame-laurel {
  background: linear-gradient(135deg, #E4C265, #FFF3B0 50%, #B08D3F);
  padding: 3px !important;
  box-shadow: 0 0 12px #E4C26588;
}
.pf-frame-laurel-decor { animation: pf-laurel-shine 3s ease-in-out infinite; }

/* Hearts — kleine hartjes rondom die zachtjes ademen */
@keyframes pf-heart-pulse { 0%,100% { transform: scale(1); opacity: .85; }
                             50%    { transform: scale(1.15); opacity: 1; } }
.pf-frame-hearts { background: linear-gradient(135deg, #EC4899, #F9A8D4, #EC4899);
                   padding: 3px !important;
                   box-shadow: 0 0 12px #ec489966; }

/* Hexagon — clip-path hexagon met neon-cyan gloed */
@keyframes pf-hex-glow { 0%,100% { filter: drop-shadow(0 0 6px #06b6d4); }
                          50%    { filter: drop-shadow(0 0 16px #22d3ee) drop-shadow(0 0 32px #06b6d4); } }
.pf-frame-hexagon {
  background: linear-gradient(135deg, #06b6d4, #67e8f9, #06b6d4);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  padding: 4px !important;
  animation: pf-hex-glow 2.4s ease-in-out infinite;
}
.pf-frame-hexagon-inner {
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  border-radius: 0 !important;
}

/* Confetti — 8 kleine gekleurde vlekjes die roteren rondom */
@keyframes pf-confetti-rotate { to { transform: translate(-50%,-50%) rotate(360deg); } }
.pf-frame-confetti { background: conic-gradient(from 0deg,
    #ef4444, #f59e0b, #eab308, #22c55e, #06b6d4, #3b82f6, #a855f7, #ec4899, #ef4444);
  padding: 4px !important; }
.pf-confetti-dot { position: absolute; top: 50%; left: 50%; width: 8px; height: 8px;
                    border-radius: 2px; animation: pf-confetti-rotate 8s linear infinite;
                    transform-origin: 50% 50%; }

/* Sakura — kersenbloesems die traag ronddraaien */
@keyframes pf-sakura-drift { to { transform: translate(-50%,-50%) rotate(-360deg); } }
.pf-frame-sakura { background: linear-gradient(135deg, #fecaca, #f9a8d4, #fecdd3);
                   padding: 3px !important;
                   box-shadow: 0 0 12px #fda4af88; }
.pf-sakura-petal { position: absolute; top: 50%; left: 50%;
                    animation: pf-sakura-drift 14s linear infinite;
                    transform-origin: 50% 50%; }

/* Wings — 👼 vleugels links en rechts, subtiele glow */
@keyframes pf-wings-flap { 0%,100% { transform: translate(-50%,-50%) scaleY(1); }
                            50%    { transform: translate(-50%,-50%) scaleY(1.08); } }
.pf-frame-wings { background: linear-gradient(135deg, #fef3c7, #fde68a 50%, #fbbf24);
                   padding: 3px !important;
                   box-shadow: 0 0 16px #fde68acc; }
.pf-wing { position: absolute; top: 50%; animation: pf-wings-flap 2.6s ease-in-out infinite;
           filter: drop-shadow(0 2px 4px rgba(0,0,0,.4)); }

/* Skulls — 💀 op 4 punten, subtiel schokkerig */
@keyframes pf-skull-shake { 0%,100% { transform: translate(-50%,-50%) rotate(-6deg); }
                             50%    { transform: translate(-50%,-50%) rotate(6deg); } }
.pf-frame-skulls { background: linear-gradient(135deg, #7f1d1d, #dc2626 50%, #450a0a);
                    padding: 3px !important;
                    box-shadow: 0 0 12px #ef444488; }
.pf-skull { position: absolute; animation: pf-skull-shake 1.4s ease-in-out infinite;
            filter: drop-shadow(0 0 3px #ef4444); }

/* Snowflakes — ❄️ die traag rondzweven */
@keyframes pf-snow-rot { to { transform: translate(-50%,-50%) rotate(360deg); } }
.pf-frame-snowflakes { background: linear-gradient(135deg, #dbeafe, #93c5fd, #dbeafe);
                       padding: 3px !important;
                       box-shadow: 0 0 14px #93c5fdaa; }
.pf-snow { position: absolute; top: 50%; left: 50%; animation: pf-snow-rot 10s linear infinite;
           transform-origin: 50% 50%;
           filter: drop-shadow(0 0 4px #fff); }

/* Runes — kleine gloeiende symbolen op 8 punten */
@keyframes pf-rune-pulse { 0%,100% { opacity: .6; filter: drop-shadow(0 0 3px #a855f7); }
                            50%    { opacity: 1;  filter: drop-shadow(0 0 8px #a855f7) drop-shadow(0 0 14px #d8b4fe); } }
.pf-frame-runes { background: linear-gradient(135deg, #1e1b4b, #4c1d95 50%, #1e1b4b);
                   padding: 4px !important;
                   box-shadow: inset 0 0 8px #a855f755; }
.pf-rune { position: absolute; top: 50%; left: 50%; color: #d8b4fe;
           font-family: 'JetBrains Mono', monospace; font-weight: 700;
           animation: pf-rune-pulse 2.4s ease-in-out infinite;
           transform-origin: 50% 50%; }

/* ── Extra banner effecten ─────────────────────────────────── */

/* Confetti-rain — kleine vierkantjes vallen door de banner */
@keyframes pf-confetti-fall {
  0%   { transform: translateY(-10%) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translateY(110%) rotate(540deg); opacity: 0; }
}
.pf-banner-confetti::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image:
    radial-gradient(circle at 12% 10%, #ef4444 0 3px, transparent 4px),
    radial-gradient(circle at 28% 30%, #22c55e 0 3px, transparent 4px),
    radial-gradient(circle at 45% 20%, #eab308 0 3px, transparent 4px),
    radial-gradient(circle at 62% 40%, #06b6d4 0 3px, transparent 4px),
    radial-gradient(circle at 78% 15%, #a855f7 0 3px, transparent 4px),
    radial-gradient(circle at 88% 35%, #ec4899 0 3px, transparent 4px),
    radial-gradient(circle at 8% 60%, #3b82f6 0 3px, transparent 4px),
    radial-gradient(circle at 32% 75%, #f59e0b 0 3px, transparent 4px);
  background-size: 100% 200%;
  animation: pf-confetti-fall 6s linear infinite;
}

/* Snowfall — kleine witte dots dwarrelen */
@keyframes pf-snow-fall {
  0%   { transform: translateY(-10%); }
  100% { transform: translateY(110%); }
}
.pf-banner-snow::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image:
    radial-gradient(circle at 10% 10%, #fff 0 1.5px, transparent 3px),
    radial-gradient(circle at 22% 40%, #e0f2fe 0 1px, transparent 2px),
    radial-gradient(circle at 38% 20%, #fff 0 1.5px, transparent 3px),
    radial-gradient(circle at 55% 60%, #dbeafe 0 1px, transparent 2px),
    radial-gradient(circle at 72% 30%, #fff 0 1.5px, transparent 3px),
    radial-gradient(circle at 88% 55%, #f0f9ff 0 1px, transparent 2px);
  background-size: 100% 200%;
  animation: pf-snow-fall 8s linear infinite;
  opacity: .85;
}

/* ── Extra badge animaties ─────────────────────────────────── */
@keyframes pf-badge-spin   { to { transform: rotate(360deg); } }
@keyframes pf-badge-bounce { 0%,100% { transform: translateY(0); }
                              50%    { transform: translateY(-3px); } }
.pf-badge-spin   { animation: pf-badge-spin 4s linear infinite; }
.pf-badge-bounce { animation: pf-badge-bounce 1.6s ease-in-out infinite; }

/* ── Banner effecten ───────────────────────────────────────── */

/* Shimmer — glans-band schuift horizontaal over de banner */
@keyframes pf-banner-shimmer {
  0%   { transform: translateX(-100%) skewX(-15deg); }
  100% { transform: translateX(200%) skewX(-15deg); }
}
.pf-banner-shimmer::after {
  content: ''; position: absolute; inset: 0; overflow: hidden;
  pointer-events: none;
  background: linear-gradient(90deg, transparent 40%, rgba(255,255,255,.25) 50%, transparent 60%);
  animation: pf-banner-shimmer 4s ease-in-out infinite;
}

/* Aurora-sweep — kleurige gloed die traag schuift */
@keyframes pf-banner-aurora {
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}
.pf-banner-aurora::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(120deg, #4ade8033, #22d3ee33, #a855f733, #ec489933, #4ade8033);
  background-size: 300% 300%;
  animation: pf-banner-aurora 10s ease-in-out infinite;
  mix-blend-mode: screen;
}

/* Scanlines — CRT-esthetiek */
.pf-banner-scan::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: repeating-linear-gradient(0deg,
    transparent 0, transparent 2px,
    rgba(0,0,0,.15) 2px, rgba(0,0,0,.15) 3px);
  mix-blend-mode: multiply;
}
@keyframes pf-banner-scan-sweep {
  0%   { transform: translateY(-100%); }
  100% { transform: translateY(200%); }
}
.pf-banner-scan::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  height: 30%;
  background: linear-gradient(180deg, transparent, rgba(74,222,128,.15), transparent);
  animation: pf-banner-scan-sweep 5s linear infinite;
}

/* Pulse — subtiele glow-ademhaling van boven */
@keyframes pf-banner-pulse { 0%, 100% { opacity: .3; } 50% { opacity: .8; } }
.pf-banner-pulse::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse at 50% -20%, var(--fc, #B4FF39)55, transparent 60%);
  animation: pf-banner-pulse 3s ease-in-out infinite;
}

/* ── Badge animaties ───────────────────────────────────────── */

/* Shine — diagonale glans-band die eens per 4s over de badge veegt */
@keyframes pf-badge-shine {
  0%, 60% { transform: translateX(-120%) rotate(20deg); }
  100%    { transform: translateX(220%) rotate(20deg); }
}
.pf-badge-shine { position: relative; overflow: hidden; }
.pf-badge-shine::after {
  content: ''; position: absolute; top: 0; bottom: 0; width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent);
  animation: pf-badge-shine 4s ease-in-out infinite;
  pointer-events: none;
}

/* Pulse — subtiel op-en-neer schalen + glow */
@keyframes pf-badge-pulse { 0%, 100% { transform: scale(1); filter: drop-shadow(0 0 3px currentColor); }
                             50%      { transform: scale(1.08); filter: drop-shadow(0 0 10px currentColor); } }
.pf-badge-pulse { animation: pf-badge-pulse 2s ease-in-out infinite; }

/* Glow — brede ademhalende halo */
@keyframes pf-badge-glow { 0%, 100% { filter: drop-shadow(0 0 4px currentColor); }
                            50%      { filter: drop-shadow(0 0 14px currentColor) drop-shadow(0 0 24px currentColor); } }
.pf-badge-glow { animation: pf-badge-glow 2.4s ease-in-out infinite; }

/* Orbit — dun draaiend ringetje eromheen */
.pf-badge-orbit { position: relative; }
.pf-badge-orbit::before {
  content: ''; position: absolute; inset: -4px; border-radius: 9999px;
  border: 1px dashed currentColor; opacity: .5;
  animation: pf-rotate 6s linear infinite;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .pf-frame-pulse, .pf-frame-rotate, .pf-frame-neon, .pf-frame-flame,
  .pf-frame-stripes, .pf-orbit-dot,
  .pf-frame-lightning, .pf-frame-aurora, .pf-frame-cosmic,
  .pf-frame-ice, .pf-frame-plasma, .pf-frame-void,
  .pf-frame-laurel-decor, .pf-frame-hexagon,
  .pf-confetti-dot, .pf-sakura-petal, .pf-wing, .pf-skull, .pf-snow, .pf-rune,
  .pf-banner-shimmer::after, .pf-banner-aurora::after,
  .pf-banner-scan::after, .pf-banner-pulse::after,
  .pf-banner-confetti::before, .pf-banner-snow::before,
  .pf-badge-shine::after, .pf-badge-pulse, .pf-badge-glow,
  .pf-badge-orbit::before, .pf-badge-spin, .pf-badge-bounce { animation: none !important; }
}
`

let _stylesInjected = false
function InjectStyles() {
  useEffect(() => {
    if (_stylesInjected) return
    _stylesInjected = true
    const s = document.createElement('style')
    s.textContent = CSS
    document.head.appendChild(s)
  }, [])
  return null
}

/* ── Hero met corner-brackets ─────────────────────────────── */

export function ProfileHero({
  accent = '#B4FF39',
  eyebrow, name, handle,
  meta,        // React-node onder de handle (bv 'lid sinds …')
  right,       // React-node rechts (bv level-badge, punten)
  avatar,      // React-node voor het portret/logo
  children,    // extra content onder de identiteit
  banner,      // profile_items entry van kind 'banner' → { config: { image_url } }
  customBannerUrl = null,   // URL uit users.custom_banner_url (eigen upload)
  bannerSource = null,      // 'custom' | 'inventory' | null (=auto)
  roleBadge = null,         // {name, description, color, icon_url, animation}
}) {
  // Bron kiezen: expliciete keuze wint; anders custom als aanwezig, anders inventory
  const inventoryUrl = banner?.config?.image_url || null
  let bannerUrl = null
  if (bannerSource === 'custom')         bannerUrl = customBannerUrl
  else if (bannerSource === 'inventory') bannerUrl = inventoryUrl
  else                                    bannerUrl = customBannerUrl || inventoryUrl
  // Effect enkel actief als de bron een inventory-banner is (custom uploads
  // hebben geen effect-config). Zo blijven admin-toegekende items het accent
  // en behoudt eigen upload zijn "zoals de user het insnijdt"-look.
  const bannerEffect = (bannerSource !== 'custom' && banner?.config?.effect) || null
  const bannerAccent = banner?.config?.color || accent
  const effectClass = {
    shimmer:  'pf-banner-shimmer',
    aurora:   'pf-banner-aurora',
    scan:     'pf-banner-scan',
    pulse:    'pf-banner-pulse',
    confetti: 'pf-banner-confetti',
    snow:     'pf-banner-snow',
  }[bannerEffect] || ''
  return (
    <>
      <InjectStyles />
      <div className={`pf-hud relative overflow-hidden rounded-[6px] border border-border ${effectClass}`}
           style={{
             minHeight: 'clamp(16rem, 32vh, 22rem)',
             '--fc': bannerAccent,
             background: bannerUrl
               // Zwaardere gradient over de tekst-zone (links-onder) zodat de
               // naam altijd leesbaar blijft, ongeacht wat er op de banner staat.
               // De rest van de banner blijft zichtbaar; enkel achter de tekst
               // wordt donker gemaakt.
               ? `radial-gradient(ellipse 70% 90% at 10% 90%, rgba(0,0,0,.75) 0%, rgba(0,0,0,.35) 45%, rgba(0,0,0,0) 75%),
                  linear-gradient(180deg, rgba(0,0,0,.20) 0%, rgba(0,0,0,.55) 100%),
                  url("${bannerUrl}") center/cover no-repeat`
               : `radial-gradient(ellipse 80% 100% at 20% 20%, ${accent}22 0%, transparent 55%),
                          radial-gradient(ellipse 60% 80% at 90% 100%, ${accent}18 0%, transparent 60%),
                          linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--background)) 100%)`,
           }}>
        {/* HUD-frame */}
        <div className="pf-bracket-nw" style={{ color: accent, borderColor: accent }} />
        <div className="pf-bracket-ne" style={{ color: accent, borderColor: accent }} />
        <div className="pf-bracket-sw" style={{ color: accent, borderColor: accent }} />
        <div className="pf-bracket-se" style={{ color: accent, borderColor: accent }} />
        {!bannerUrl && <div className="pf-hero-grid" />}

        {/* Rol-badge — prominent floating rechtsboven op de banner. */}
        {roleBadge && (
          <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
            <div className="relative flex items-center gap-2 px-3 py-2 rounded-full backdrop-blur-sm"
                 style={{
                   background: `${roleBadge.color || '#B4FF39'}18`,
                   border: `1.5px solid ${roleBadge.color || '#B4FF39'}`,
                   boxShadow: `0 0 20px ${roleBadge.color || '#B4FF39'}66, 0 0 40px ${roleBadge.color || '#B4FF39'}33, inset 0 1px 0 rgba(255,255,255,.2)`,
                 }}
                 title={roleBadge.description || roleBadge.name}>
              <div className={`h-6 w-6 md:h-7 md:w-7 grid place-items-center shrink-0 ${
                roleBadge.animation === 'pulse' ? 'pf-badge-pulse' :
                roleBadge.animation === 'glow'  ? 'pf-badge-glow'  :
                roleBadge.animation === 'orbit' ? 'pf-badge-orbit' :
                roleBadge.animation === 'spin'  ? 'pf-badge-spin'  : ''}`}
                   style={{ color: roleBadge.color }}>
                {renderBadgeIcoon(roleBadge.icon_url, 'h-full w-full') || <Icons.Shield className="h-full w-full" />}
              </div>
              <span className="pf-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] font-semibold hidden sm:inline"
                    style={{ color: roleBadge.color, textShadow: `0 0 8px ${roleBadge.color}88` }}>
                {roleBadge.name}
              </span>
            </div>
          </div>
        )}

        <div className="relative p-6 md:p-8 flex items-center gap-6 flex-wrap min-h-full">
          {avatar}
          <div className="flex-1 min-w-[16rem]">
            {eyebrow && (
              <div className="pf-mono text-[11px] uppercase tracking-[0.35em] mb-2"
                   style={{
                     color: accent,
                     textShadow: bannerUrl ? '0 1px 3px rgba(0,0,0,.9), 0 0 8px rgba(0,0,0,.5)' : undefined,
                   }}>{eyebrow}</div>
            )}
            <h1 className="pf-name text-[clamp(3rem,7vw,6rem)] text-foreground"
                style={{
                  textShadow: bannerUrl
                    // Zware drop-shadow + subtiele accent-glow zodat de naam
                    // ook op een felle of drukke banner leesbaar blijft.
                    ? `0 2px 6px rgba(0,0,0,.95), 0 4px 20px rgba(0,0,0,.75), 0 0 30px ${accent}33`
                    : `0 2px 20px ${accent}22`,
                }}>
              {name}
            </h1>
            {handle && (
              <div className="pf-mono text-sm mt-2"
                   style={{
                     color: bannerUrl ? 'rgba(255,255,255,.85)' : undefined,
                     textShadow: bannerUrl ? '0 1px 3px rgba(0,0,0,.9)' : undefined,
                   }}>{handle}</div>
            )}
            {meta && (
              <div className="mt-3 text-sm"
                   style={{
                     color: bannerUrl ? 'rgba(255,255,255,.85)' : undefined,
                     textShadow: bannerUrl ? '0 1px 3px rgba(0,0,0,.9)' : undefined,
                   }}>{meta}</div>
            )}
          </div>
          {right && <div className="shrink-0">{right}</div>}
        </div>

        {children && (
          <div className="relative px-6 md:px-8 pb-6">{children}</div>
        )}
      </div>
    </>
  )
}

/* ── Grote stat-strip (rij van numerieke tegels) ─────────── */

export function StatStrip({ items, accent = '#B4FF39' }) {
  return (
    <div className="pf-hud grid gap-px bg-border overflow-hidden rounded-[6px] border border-border"
         style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((it, i) => {
        const Icon = it.icon
        return (
          <div key={i} className="bg-card p-4 md:p-5 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {Icon && <Icon className="h-3.5 w-3.5" style={{ color: it.color || accent }} />}
              <span className="pf-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {it.label}
              </span>
            </div>
            <div className="pf-name text-[clamp(1.75rem,4vw,3rem)] tabular-nums"
                 style={{ color: it.color || accent }}>
              {it.value}
            </div>
            {it.sub && (
              <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {it.sub}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ── Trophy Hall — horizontale rij van compo-medailles ─────
   Elke medaille is een "trading card": kleur op basis van plaats
   (goud/zilver/brons), kleine ribbon met event-naam. */

const PLAATS_KLEUR = { 1: '#E4C265', 2: '#C0C0C0', 3: '#CD7F32' }

export function TrophyHall({ results = [], isOwner, onToggleHidden, onMove }) {
  const [uitgeklapt, setUitgeklapt] = useState(false)
  const [beheerModus, setBeheerModus] = useState(false)
  const podia = results.filter(r => r.place <= 3)
  const zichtbareLijst = beheerModus ? podia : podia.filter(r => !r._hidden)
  const verborgenAantal = podia.filter(r => r._hidden).length
  if (podia.length === 0) return null
  const perPagina = 10                      // 5 kolommen × 2 rijen
  const zichtbaar = (uitgeklapt || beheerModus) ? zichtbareLijst : zichtbareLijst.slice(0, perPagina)
  const meerAantal = zichtbareLijst.length - perPagina
  return (
    <div className="pf-hud">
      <SectionHeader
        label="Trophy Hall"
        count={podia.length}
        action={
          <div className="flex items-center gap-3">
            {isOwner && podia.length > 0 && (
              <button onClick={() => setBeheerModus(v => !v)}
                      className={`pf-mono text-[10px] uppercase tracking-widest transition-colors ${
                        beheerModus ? 'text-acid' : 'text-muted-foreground hover:text-acid'}`}>
                {beheerModus ? '✓ Klaar' : 'Beheer'}
              </button>
            )}
            {isOwner && verborgenAantal > 0 && !beheerModus && (
              <span className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {verborgenAantal} verborgen
              </span>
            )}
            {!beheerModus && meerAantal > 0 && (
              <button onClick={() => setUitgeklapt(v => !v)}
                      className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-acid transition-colors">
                {uitgeklapt ? '− Inklappen' : `+ Toon alle (${meerAantal} meer)`}
              </button>
            )}
          </div>
        }
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {zichtbaar.map((r, i) => {
          const kleur = PLAATS_KLEUR[r.place] || '#94A3B8'
          const SoortIcon = r.kind === 'kempenvision' ? Icons.Radio :
                            r.kind === 'imagecompo'   ? Icons.Sparkles :
                                                        Icons.Trophy
          const trophyKey = `${r.kind}:${r.source_id}`
          return (
            <div key={i}
                 className={`rounded-[6px] border border-border bg-card p-3 relative overflow-hidden ${r._hidden ? 'opacity-40' : ''}`}
                 style={{ borderTop: `3px solid ${kleur}` }}>
              <div className="absolute top-0 right-2 pf-name text-[3rem] leading-none opacity-15"
                   style={{ color: kleur }}>
                {r.place}
              </div>
              <div className="relative">
                <div className="flex items-center gap-1.5 mb-3">
                  <SoortIcon className="h-3.5 w-3.5" style={{ color: kleur }} />
                  <span className="pf-mono text-[9px] uppercase tracking-widest"
                        style={{ color: kleur }}>
                    {r.meta?.label
                      ? r.meta.label
                      : r.place === 1 ? 'Goud' : r.place === 2 ? 'Zilver' : 'Brons'}
                  </span>
                </div>
                <div className="text-sm font-semibold leading-tight line-clamp-2 min-h-[2.5rem]">
                  {r.source_name}
                </div>
                <div className="pf-mono text-[10px] text-muted-foreground mt-2">
                  {trofeeDatumLabel(r)}
                </div>
              </div>
              {beheerModus && isOwner && (
                <div className="absolute bottom-1 right-1 flex items-center gap-0.5 bg-card/95 rounded border border-border">
                  <button onClick={() => onMove?.(trophyKey, -1)} disabled={i === 0}
                          className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Naar voor">
                    <Icons.ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => onMove?.(trophyKey, 1)} disabled={i === zichtbaar.length - 1}
                          className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Naar achter">
                    <Icons.ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => onToggleHidden?.(trophyKey, !r._hidden)}
                          className="p-0.5 rounded hover:bg-accent"
                          title={r._hidden ? 'Weer tonen' : 'Verbergen'}>
                    {r._hidden
                      ? <Icons.Eye className="h-3.5 w-3.5" />
                      : <Icons.EyeOff className="h-3.5 w-3.5" />}
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Achievement panel — badges grid ─────────────────────── */

export function AchievementPanel({ badges = [], onAdd, onRemove, canManage,
                                   isOwner, onToggleHidden, onMove }) {
  const [beheerModus, setBeheerModus] = useState(false)
  const [toonVerborgen, setToonVerborgen] = useState(false)
  const zichtbaar = beheerModus || toonVerborgen ? badges : badges.filter(b => !b.hidden)
  const verborgenAantal = badges.filter(b => b.hidden).length
  return (
    <div className="pf-hud">
      <SectionHeader label="Achievements" count={badges.length} action={
        <div className="flex items-center gap-3">
          {isOwner && badges.length > 0 && (
            <button onClick={() => setBeheerModus(v => !v)}
                    className={`pf-mono text-[10px] uppercase tracking-widest transition-colors ${
                      beheerModus ? 'text-acid' : 'text-muted-foreground hover:text-acid'}`}>
              {beheerModus ? '✓ Klaar' : 'Beheer'}
            </button>
          )}
          {isOwner && verborgenAantal > 0 && !beheerModus && (
            <button onClick={() => setToonVerborgen(v => !v)}
                    className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-acid transition-colors">
              {toonVerborgen ? `Verberg (${verborgenAantal})` : `Toon verborgen (${verborgenAantal})`}
            </button>
          )}
          {onAdd && canManage && (
            <button onClick={onAdd}
                    className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-acid transition-colors">
              + Toekennen
            </button>
          )}
        </div>
      } />
      {zichtbaar.length === 0 ? (
        <p className="pf-mono text-xs text-muted-foreground">Nog niks verdiend.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {zichtbaar.map((b, idx) => {
            const cardAnim = b.animation === 'shine' ? 'pf-badge-shine' : ''
            const iconAnim = {
              pulse:  'pf-badge-pulse',
              glow:   'pf-badge-glow',
              orbit:  'pf-badge-orbit',
              spin:   'pf-badge-spin',
              bounce: 'pf-badge-bounce',
            }[b.animation] || ''
            return (
              <div key={b.id}
                   className={`group relative p-3 rounded-[4px] border border-border bg-card flex items-center gap-2 ${cardAnim} ${b.hidden ? 'opacity-40' : ''}`}
                   style={{ borderLeft: `2px solid ${b.color || '#B4FF39'}` }}
                   title={b.description || b.name}>
                <div className={`h-8 w-8 rounded-[3px] grid place-items-center shrink-0 ${iconAnim}`}
                     style={{ background: `${b.color || '#B4FF39'}18`, color: b.color || '#B4FF39' }}>
                  {renderBadgeIcoon(b.icon_url, 'h-5 w-5') || <Icons.Award className="h-4 w-4" />}
                </div>
                <div className="text-xs font-semibold truncate flex-1">{b.name}</div>
                {beheerModus && isOwner && (
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => onMove?.(b.id, -1)} disabled={idx === 0}
                            className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Omhoog">
                      <Icons.ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => onMove?.(b.id, 1)} disabled={idx === zichtbaar.length - 1}
                            className="p-0.5 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Omlaag">
                      <Icons.ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => onToggleHidden?.(b.id, !b.hidden)}
                            className="p-0.5 rounded hover:bg-accent"
                            title={b.hidden ? 'Weer tonen' : 'Verbergen'}>
                      {b.hidden
                        ? <Icons.Eye className="h-3.5 w-3.5" />
                        : <Icons.EyeOff className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                )}
                {!beheerModus && canManage && onRemove && (
                  <button onClick={() => onRemove(b.id)}
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          title="Terugtrekken">
                    <Icons.X className="h-3 w-3 text-signal" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Match Log — recente compo-uitslagen als monospaced-tabel ── */

export function MatchLog({ results = [] }) {
  if (results.length === 0) return null
  const grouped = new Map()
  for (const r of results) {
    const k = `${r.kind}-${r.source_id ?? r.source_name}`
    if (!grouped.has(k)) grouped.set(k, r)
  }
  const rijen = [...grouped.values()].slice(0, 12)

  return (
    <div className="pf-hud">
      <SectionHeader label="Match log" count={rijen.length} />
      <div className="rounded-[6px] border border-border bg-card overflow-hidden">
        {rijen.map((r, i) => {
          const kleur = PLAATS_KLEUR[r.place] || '#94A3B8'
          const SoortIcon = r.kind === 'kempenvision' ? Icons.Radio :
                            r.kind === 'imagecompo'   ? Icons.Sparkles :
                                                        Icons.Trophy
          const plaatsLabel = r.meta?.label
            ? r.meta.label
            : r.place === 1 ? '1st' : r.place === 2 ? '2nd' : r.place === 3 ? '3rd' : `${r.place}th`
          return (
            <div key={i} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border/50' : ''}`}>
              <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground w-24 shrink-0 truncate"
                   title={r.edition_name || ''}>
                {trofeeDatumLabel(r, { style: 'short' })}
              </div>
              <SoortIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0 text-sm truncate">{r.source_name}</div>
              <div className="pf-mono text-[11px] tabular-nums shrink-0" style={{ color: kleur }}>
                {plaatsLabel}
              </div>
              {r.points > 0 && (
                <div className="pf-mono text-[11px] tabular-nums shrink-0 text-acid w-14 text-right">
                  +{r.points} pt
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── Sectie-header met numerieke telling ─────────────────── */

export function SectionHeader({ label, count, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-baseline gap-2">
        <span className="pf-name text-lg text-foreground">{label}</span>
        {count != null && (
          <span className="pf-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            [{String(count).padStart(2, '0')}]
          </span>
        )}
      </div>
      {action}
    </div>
  )
}

/* ── Avatar-blok (rond, dubbele rand) ────────────────────── */

/* Beschikbare frame-stijlen. Nieuwe animated variants gebruiken keyframes
   uit de globale CSS hierboven (respecteert prefers-reduced-motion). */
export const FRAME_STYLES = [
  // Statische basis
  'ring', 'double', 'dashed', 'glow',
  // Animated basis
  'pulse', 'rotate', 'neon', 'orbit', 'stripes',
  // Legendary/epic effect-frames
  'crown', 'flame', 'lightning', 'aurora', 'cosmic', 'ice', 'plasma', 'void',
  // Ornate/thematische frames met decor rondom
  'laurel', 'hearts', 'hexagon', 'confetti', 'sakura',
  'wings', 'skulls', 'snowflakes', 'runes',
]

// Metadata over de frames (voor admin-picker + tooltips).
export const FRAME_META = {
  ring:       { label: 'Ring',            rarity: 'common',    animated: false },
  double:     { label: 'Double',          rarity: 'common',    animated: false },
  dashed:     { label: 'Dashed',          rarity: 'common',    animated: false },
  crown:      { label: 'Kroontje 👑',     rarity: 'legendary', animated: false },
  glow:       { label: 'Glow',            rarity: 'rare',      animated: false },
  pulse:      { label: 'Pulse',           rarity: 'rare',      animated: true  },
  rotate:     { label: 'Rotate',          rarity: 'rare',      animated: true  },
  neon:       { label: 'Neon',            rarity: 'epic',      animated: true  },
  orbit:      { label: 'Orbit',           rarity: 'epic',      animated: true  },
  flame:      { label: 'Vlammen 🔥',      rarity: 'epic',      animated: true  },
  stripes:    { label: 'Stripes',         rarity: 'common',    animated: true  },
  lightning:  { label: 'Bliksem ⚡',      rarity: 'legendary', animated: true  },
  aurora:     { label: 'Aurora 🌌',       rarity: 'legendary', animated: true  },
  cosmic:     { label: 'Cosmic ✨',       rarity: 'legendary', animated: true  },
  ice:        { label: 'IJs ❄️',          rarity: 'epic',      animated: true  },
  plasma:     { label: 'Plasma',          rarity: 'legendary', animated: true  },
  void:       { label: 'Void 🌑',         rarity: 'legendary', animated: true  },
  // Ornate
  laurel:     { label: 'Laurierkrans 🏆', rarity: 'legendary', animated: true  },
  hearts:     { label: 'Hartjes 💕',       rarity: 'epic',      animated: true  },
  hexagon:    { label: 'Hexagon ⬢',        rarity: 'epic',      animated: true  },
  confetti:   { label: 'Confetti 🎉',      rarity: 'legendary', animated: true  },
  sakura:     { label: 'Sakura 🌸',        rarity: 'epic',      animated: true  },
  wings:      { label: 'Vleugels 🕊️',      rarity: 'legendary', animated: true  },
  skulls:     { label: 'Skulls 💀',        rarity: 'epic',      animated: true  },
  snowflakes: { label: 'Sneeuw ❄️',        rarity: 'epic',      animated: true  },
  runes:      { label: 'Runen ᛟ',         rarity: 'legendary', animated: true  },
}

export function Avatar({ url, name, size = 128, accent = '#B4FF39', frame, shape = 'round' }) {
  const initials = (name || '?').trim().slice(0, 2).toUpperCase()
  const fc = frame?.config?.color || accent
  const style = frame?.config?.style || 'ring'
  const radius = shape === 'square' ? '10px' : '9999px'

  // De outer wrap = het frame. Inner circle/square = de foto zelf.
  const wrap = { padding: 3, borderRadius: radius, position: 'absolute', inset: 0 }
  let className = ''
  const cssVar = { '--fc': fc }

  switch (style) {
    case 'double':
      wrap.padding = 5
      wrap.background = `linear-gradient(135deg, ${fc}, transparent 45%, ${fc} 100%)`
      break
    case 'dashed':
      wrap.padding = 4; wrap.background = 'transparent'
      wrap.boxShadow = `inset 0 0 0 3px ${fc}`
      wrap.outline = `2px dashed ${fc}88`; wrap.outlineOffset = '3px'
      break
    case 'crown':
      wrap.padding = 4
      wrap.background = `conic-gradient(from 210deg, ${fc}, ${fc}22, ${fc}, ${fc}22, ${fc})`
      break
    case 'glow':
      wrap.padding = 3
      wrap.background = `linear-gradient(135deg, ${fc}, transparent 60%)`
      wrap.boxShadow = `0 0 32px ${fc}88, 0 0 8px ${fc}`
      break
    case 'pulse':
      wrap.padding = 3
      wrap.background = `linear-gradient(135deg, ${fc}, transparent 50%)`
      className = 'pf-frame-pulse'
      break
    case 'rotate':
      wrap.padding = 3
      wrap.background = `conic-gradient(from 0deg, ${fc}, transparent 30%, ${fc} 60%, transparent 90%, ${fc})`
      className = 'pf-frame-rotate'
      break
    case 'neon':
      wrap.padding = 3; wrap.background = 'transparent'
      wrap.boxShadow = `inset 0 0 0 2px ${fc}`
      className = 'pf-frame-neon'
      break
    case 'flame':
      wrap.padding = 4
      wrap.background = `conic-gradient(from 200deg, #ff5a5a, #ff9500, #ffcf40, #ff9500, #ff5a5a)`
      className = 'pf-frame-flame'
      break
    case 'stripes':
      wrap.padding = 4; wrap.background = 'transparent'
      className = 'pf-frame-stripes'
      break
    case 'lightning':
      className = 'pf-frame-lightning'
      break
    case 'aurora':
      className = 'pf-frame-aurora'
      break
    case 'cosmic':
      className = 'pf-frame-cosmic'
      break
    case 'ice':
      className = 'pf-frame-ice'
      break
    case 'plasma':
      className = 'pf-frame-plasma'
      break
    case 'void':
      className = 'pf-frame-void'
      break
    case 'laurel':
      className = 'pf-frame-laurel'
      break
    case 'hearts':
      className = 'pf-frame-hearts'
      break
    case 'hexagon':
      className = 'pf-frame-hexagon'
      // Inner clip-path zodat de foto ook hexagon-vorm krijgt
      break
    case 'confetti':
      className = 'pf-frame-confetti'
      break
    case 'sakura':
      className = 'pf-frame-sakura'
      break
    case 'wings':
      className = 'pf-frame-wings'
      break
    case 'skulls':
      className = 'pf-frame-skulls'
      break
    case 'snowflakes':
      className = 'pf-frame-snowflakes'
      break
    case 'runes':
      className = 'pf-frame-runes'
      break
    case 'ring':
    default:
      wrap.padding = 3
      wrap.background = `linear-gradient(135deg, ${fc}, transparent 50%)`
      break
  }

  // Inner clip → foto meeknippen als frame een non-cirkel vorm heeft (bv. hexagon).
  const innerStyle = { color: fc, borderRadius: shape === 'square' ? '8px' : '9999px' }
  if (style === 'hexagon') {
    innerStyle.clipPath = 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
    innerStyle.borderRadius = 0
  }
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className={className} style={{ ...wrap, ...cssVar }}>
        <div className="w-full h-full overflow-hidden bg-background grid place-items-center pf-name text-4xl"
             style={innerStyle}>
          {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : initials}
        </div>
      </div>
      {style === 'orbit' && (
        <>
          <span className="pf-orbit-dot" style={cssVar} />
          <span className="pf-orbit-dot d2" style={cssVar} />
          <span className="pf-orbit-dot d3" style={cssVar} />
        </>
      )}
      {style === 'crown' && (
        // Kroon binnen de avatar-wrap tekenen (top: 0) i.p.v. eroverheen (-top-3);
        // de parent hero heeft overflow-hidden zodat de banner-gradient netjes
        // klipt tegen de border-radius, en dan zou een negatieve top clippen.
        <span aria-hidden
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/3 top-0 select-none pointer-events-none"
              style={{
                fontSize: Math.max(20, Math.round(size * 0.28)),
                filter: `drop-shadow(0 2px 3px rgba(0,0,0,.55)) drop-shadow(0 0 8px ${fc}66)`,
              }}>
          👑
        </span>
      )}
      <FrameDecor style={style} size={size} />
    </div>
  )
}

/* Decor-overlay per frame — geplaatst BOVEN de avatar-wrap zodat de emoji's
   niet door de border-radius geklipt worden. Alle absolute-positioned relatief
   aan het Avatar-relative wrap (size × size). */
function FrameDecor({ style, size }) {
  const radius = size / 2
  // Hulp: emoji op N punten rond de cirkel (0° = boven; klokwijzer)
  const ring = (n, radiusFactor = 0.55, startAngle = -90) =>
    Array.from({ length: n }, (_, i) => {
      const a = ((startAngle + (360 / n) * i) * Math.PI) / 180
      const r = radius + radius * radiusFactor
      return { x: radius + Math.cos(a) * r, y: radius + Math.sin(a) * r, angle: a * 180 / Math.PI + 90 }
    })

  if (style === 'laurel') {
    // Twee laurel-bogen links en rechts, in goud
    const em = Math.max(14, Math.round(size * 0.22))
    return (
      <>
        <span aria-hidden className="pf-frame-laurel-decor absolute pointer-events-none select-none"
              style={{ left: -em * 0.4, top: '50%', transform: 'translateY(-50%) scaleX(-1)', fontSize: em * 2.1, lineHeight: 1 }}>🌿</span>
        <span aria-hidden className="pf-frame-laurel-decor absolute pointer-events-none select-none"
              style={{ right: -em * 0.4, top: '50%', transform: 'translateY(-50%)', fontSize: em * 2.1, lineHeight: 1 }}>🌿</span>
      </>
    )
  }
  if (style === 'hearts') {
    const em = Math.max(10, Math.round(size * 0.13))
    return ring(8, 0.05).map((p, i) => (
      <span key={i} aria-hidden className="absolute pointer-events-none select-none"
            style={{ left: p.x, top: p.y, transform: `translate(-50%,-50%) rotate(${p.angle}deg)`,
                     fontSize: em, animation: `pf-heart-pulse 1.6s ease-in-out infinite ${i * 0.15}s` }}>💕</span>
    ))
  }
  if (style === 'confetti') {
    // 8 gekleurde vlekjes met een offset op de rotating ring — puur kleur, geen emoji
    const colors = ['#ef4444','#f59e0b','#eab308','#22c55e','#06b6d4','#3b82f6','#a855f7','#ec4899']
    return colors.map((c, i) => (
      <span key={i} className="pf-confetti-dot"
            style={{ background: c, animationDelay: `${-i * 0.4}s`,
                     transform: `translate(-50%,-50%) rotate(${i * 45}deg) translateX(${radius + 4}px)` }} />
    ))
  }
  if (style === 'sakura') {
    const em = Math.max(12, Math.round(size * 0.14))
    return Array.from({ length: 6 }, (_, i) => (
      <span key={i} aria-hidden className="pf-sakura-petal pointer-events-none select-none"
            style={{ fontSize: em, animationDelay: `${-i * 2}s`,
                     transform: `translate(-50%,-50%) rotate(${i * 60}deg) translateX(${radius + 6}px)` }}>🌸</span>
    ))
  }
  if (style === 'wings') {
    const em = Math.max(16, Math.round(size * 0.34))
    return (
      <>
        <span aria-hidden className="pf-wing pointer-events-none select-none"
              style={{ left: -em * 0.55, fontSize: em, transform: 'translate(-50%,-50%) scaleX(-1)' }}>🕊️</span>
        <span aria-hidden className="pf-wing pointer-events-none select-none"
              style={{ right: -em * 0.55, fontSize: em, transform: 'translate(50%,-50%)' }}>🕊️</span>
      </>
    )
  }
  if (style === 'skulls') {
    const em = Math.max(11, Math.round(size * 0.15))
    return ring(4, 0.02, -90).map((p, i) => (
      <span key={i} aria-hidden className="pf-skull pointer-events-none select-none"
            style={{ left: p.x, top: p.y, fontSize: em, animationDelay: `${i * 0.35}s` }}>💀</span>
    ))
  }
  if (style === 'snowflakes') {
    const em = Math.max(10, Math.round(size * 0.13))
    return Array.from({ length: 6 }, (_, i) => (
      <span key={i} aria-hidden className="pf-snow pointer-events-none select-none"
            style={{ fontSize: em, animationDelay: `${-i * 1.8}s`,
                     transform: `translate(-50%,-50%) rotate(${i * 60}deg) translateX(${radius + 4}px)` }}>❄️</span>
    ))
  }
  if (style === 'runes') {
    const glyphs = ['ᛟ','ᚱ','ᚨ','ᚦ','ᛗ','ᚹ','ᛚ','ᛞ']
    const em = Math.max(10, Math.round(size * 0.13))
    return ring(8, 0.02).map((p, i) => (
      <span key={i} aria-hidden className="pf-rune pointer-events-none select-none"
            style={{ left: p.x, top: p.y, fontSize: em,
                     transform: `translate(-50%,-50%) rotate(${p.angle}deg)`,
                     animationDelay: `${i * 0.3}s` }}>{glyphs[i]}</span>
    ))
  }
  return null
}
